import { useMemo, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import type { InstanceId, ItemId, ProductId, RoomId } from '@/core/content/ids';
import type { ItemDef } from '@/core/content/schemas/items';
import type { Game } from '@/game/Game';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import { swatchFor } from './itemSwatch';
import styles from './BagTray.module.css';
import { ItemThumb } from '@/ui/art/ItemThumb';
import { fold } from '@/ui/util/search';
import { Icon } from './Icon';
import { SpotArt } from '@/ui/art/SpotArt';
import { BagItemButton } from './BagItemButton';
import { currentRoom } from '@/ui/util/room';
import { useSelectionShown } from './SelectionCard';

type Group =
  | { kind: 'instance'; itemId: ItemId; instanceIds: InstanceId[] }
  | { kind: 'product'; itemId: ProductId; count: number };

type Filter = 'all' | 'furniture' | 'decor' | 'product' | 'placed';

/** The filter row appears once the bag holds enough to need it (or a filter is on). */
const TOOLS_FROM = 6;

function matchesFilter(def: ItemDef, filter: Filter): boolean {
  if (filter === 'furniture') return def.kind === 'furniture' || def.kind === 'fixture';
  if (filter === 'decor') return def.kind === 'decor';
  if (filter === 'product') return def.kind === 'product';
  return true;
}

/**
 * S05 Túi đồ (ux-screens §3.5), as a tray: items grouped, ★ favourites first. Swipe sideways to browse,
 * pull upward to drag into the room; Enter / Space places it on the free spot nearest the middle instead
 * (no drag needed). Search, kind filters, "★ only" and "Đã đặt" (placed items, tap to go there) show once
 * the bag is big enough. In the Tiệm, product units come first: drag them onto shelves.
 */
export function BagTray({ game }: { game: Game }) {
  const { t } = useI18n();
  const { content } = useServices();
  const instances = useSave((s) => s.inventory.instances);
  const stacks = useSave((s) => s.inventory.productStacks);
  const favorites = useSave((s) => s.inventory.favorites);
  const room = useSave(currentRoom);
  const boxesLeft = useSave(
    (s) => Object.values(s.inventory.boxes).filter((b) => !b.opened && b.roomId === room).length,
  );
  // The selected item's card covers the tray; hidden, the tray also leaves the tab order meanwhile.
  const covered = useSelectionShown(game);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [favOnly, setFavOnly] = useState(false);

  const groups = useMemo(() => {
    const favs = new Set<string>(favorites);
    const byFav = (a: Group, b: Group) =>
      Number(favs.has(b.itemId)) - Number(favs.has(a.itemId)) || a.itemId.localeCompare(b.itemId);
    const map = new Map<ItemId, InstanceId[]>();
    for (const inst of Object.values(instances)) {
      if (inst.location.kind !== 'bag') continue;
      map.set(inst.itemId, [...(map.get(inst.itemId) ?? []), inst.instanceId]);
    }
    const things = [...map.entries()]
      .map(([itemId, instanceIds]): Group => ({ kind: 'instance', itemId, instanceIds }))
      .sort(byFav);
    if (room !== 'minimart') return things;
    const products = (Object.entries(stacks) as [ProductId, number][])
      .filter(([, n]) => n > 0)
      .map(([itemId, count]): Group => ({ kind: 'product', itemId, count }))
      .sort(byFav);
    return [...products, ...things];
  }, [instances, stacks, room, favorites]);

  const placed = useMemo(() => {
    const out: { roomId: RoomId; instanceId: InstanceId; itemId: ItemId }[] = [];
    for (const inst of Object.values(instances))
      if (inst.location.kind === 'placed')
        out.push({ roomId: inst.location.roomId, instanceId: inst.instanceId, itemId: inst.itemId });
    return out;
  }, [instances]);

  const miniGameRunning = useStore(game.miniGameUi, (s) => s.run !== null);
  if (miniGameRunning) return null;

  const q = fold(query);
  const keep = (itemId: ItemId) => {
    const def = content.items.get(itemId);
    if (!def) return false;
    if (favOnly && !favorites.includes(itemId)) return false;
    if (q && !fold(t(def.nameKey)).includes(q)) return false;
    return matchesFilter(def, filter);
  };
  const filtering = q !== '' || favOnly || filter !== 'all';
  const showTools = filtering || groups.length + placed.length >= TOOLS_FROM;
  const shown = groups.filter((g) => keep(g.itemId));
  const filters: Filter[] =
    room === 'minimart'
      ? ['all', 'furniture', 'decor', 'product', 'placed']
      : ['all', 'furniture', 'decor', 'placed'];

  const chip = (def: ItemDef, count: number, fav: boolean) => (
    <>
      <span className={styles.swatch} style={{ background: swatchFor(def) }} aria-hidden="true">
        <ItemThumb itemId={def.id} />
      </span>
      <span className={styles.name}>{t(def.nameKey)}</span>
      {fav && (
        <span className={styles.star} role="img" aria-label={t('ui.bag.favorite')}>
          <Icon name="star" size={18} />
        </span>
      )}
      {count > 1 && <span className={styles.count}>{t('ui.bag.count', { count })}</span>}
    </>
  );

  return (
    <section
      className={styles.tray}
      aria-label={t('ui.bag.title')}
      data-testid="bag-tray"
      style={covered ? { visibility: 'hidden' } : undefined}
    >
      {showTools && (
        <div className={styles.tools}>
          <input
            type="search"
            className={styles.search}
            value={query}
            placeholder={t('ui.bag.search')}
            aria-label={t('ui.bag.search')}
            data-testid="bag-search"
            onChange={(e) => {
              setQuery(e.target.value);
            }}
          />
          <button
            type="button"
            className={styles.filter}
            aria-pressed={favOnly}
            aria-label={t('ui.bag.fav_only')}
            data-testid="bag-fav-only"
            onClick={() => {
              setFavOnly(!favOnly);
            }}
          >
            <Icon name="star" size={22} />
          </button>
          <div className={styles.filters} role="group" aria-label={t('ui.bag.filters')}>
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                className={styles.filter}
                aria-pressed={filter === f}
                data-testid={`bag-filter-${f}`}
                onClick={() => {
                  setFilter(f);
                }}
              >
                {t(`ui.bag.filter.${f}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      {filter === 'placed' ? (
        <PlacedList
          game={game}
          placed={placed.filter((p) => keep(p.itemId))}
          favorites={favorites}
          chip={chip}
        />
      ) : shown.length === 0 ? (
        <div className={styles.emptyRow}>
          {!(filtering && groups.length > 0) && <SpotArt name="empty_bag" fallback="bag" size={64} />}
          <p className={styles.empty}>
            {filtering && groups.length > 0
              ? t('ui.bag.no_match')
              : boxesLeft > 0
                ? t('ui.bag.empty')
                : room === 'minimart'
                  ? t('ui.bag.empty_shop')
                  : t('ui.bag.all_done')}
          </p>
        </div>
      ) : (
        <>
          <p className={styles.hint}>
            {room === 'minimart' ? t('ui.bag.drag_hint_shop') : t('ui.bag.drag_hint')}
          </p>
          <ul className={styles.list}>
            {shown.map((group) => {
              const def = content.items.get(group.itemId);
              if (!def) return null;
              const count = group.kind === 'product' ? group.count : group.instanceIds.length;
              return (
                <li key={group.itemId}>
                  <BagItemButton
                    type="button"
                    className={styles.chip}
                    data-testid={`bag-item-${group.itemId}`}
                    data-kind={group.kind}
                    onDragStart={(pointer) => {
                      if (group.kind === 'product') game.startProductFromBag(group.itemId, pointer);
                      else if (group.instanceIds[0]) game.startDragFromBag(group.instanceIds[0], pointer);
                    }}
                    onClick={(e) => {
                      // Keyboard activation only: tapping or scrolling must never place an item.
                      if (e.detail !== 0) return;
                      if (group.kind === 'product') game.displayFromBag(group.itemId);
                      else if (group.instanceIds[0]) game.placeFromBag(group.instanceIds[0]);
                    }}
                  >
                    {chip(def, count, favorites.includes(group.itemId))}
                  </BagItemButton>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

/** "Đã đặt": placed items grouped by room ("Nhà: 14", "Tiệm: 6"); tapping one goes there and selects it. */
function PlacedList({
  game,
  placed,
  favorites,
  chip,
}: {
  game: Game;
  placed: { roomId: RoomId; instanceId: InstanceId; itemId: ItemId }[];
  favorites: readonly ItemId[];
  chip: (def: ItemDef, count: number, fav: boolean) => ReactNode;
}) {
  const { t } = useI18n();
  const { content } = useServices();
  if (placed.length === 0) return <p className={styles.empty}>{t('ui.bag.no_match')}</p>;
  return (
    <ul className={styles.list}>
      {(['home', 'minimart', 'studio'] as const).map((roomId) => {
        const here = placed.filter((p) => p.roomId === roomId);
        if (here.length === 0) return null;
        return [
          <li key={roomId} className={styles.roomLabel}>
            {t('ui.bag.room_count', { room: roomId, count: here.length })}
          </li>,
          ...here.map((p) => {
            const def = content.items.get(p.itemId);
            if (!def) return null;
            return (
              <li key={p.instanceId}>
                <button
                  type="button"
                  className={styles.chip}
                  data-testid={`bag-placed-${p.instanceId}`}
                  aria-label={t('ui.bag.go_there', { name: t(def.nameKey) })}
                  onClick={() => {
                    void game.showPlaced(roomId, p.instanceId);
                  }}
                >
                  {chip(def, 1, favorites.includes(p.itemId))}
                </button>
              </li>
            );
          }),
        ];
      })}
    </ul>
  );
}
